import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import copyToClip from 'clipboard-copy';
import { ApiKey, ApiService } from 'src/app/services/api/api.service';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { ErrorSnackbarComponent } from 'src/app/snackbars/error-snackbar/error-snackbar.component';
import { DeleteKeyDialogComponent } from '../../../../dialogs/api-keys/delete-key-dialog/delete-key-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatCardSubtitle,
  MatCard,
  MatCardTitle,
  MatCardActions,
} from '@angular/material/card';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-api-integrations',
  templateUrl: './api-integrations.component.html',
  styleUrls: ['./api-integrations.component.scss'],
  imports: [
    MatProgressSpinner,
    MatCardSubtitle,
    PayeeButtonComponent,
    MatCard,
    MatCardTitle,
    MatCardActions,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    TranslateModule,
    DatePipe,
  ],
})
export class ApiIntegrationsComponent implements OnInit, OnDestroy {
  private snackBar = inject(MatSnackBar);
  private apiService = inject(ApiService);
  private dialog = inject(MatDialog);
  private clientsService = inject(ClientsService);
  private translate = inject(TranslateService);

  apiKeys: ApiKey[] = [];
  readonly loading = signal(false);
  clientSubscription!: Subscription;

  async ngOnInit() {
    this.clientSubscription =
      this.clientsService.selectedClientSubject.subscribe(async () => {
        await this.loadAllKeys();
      });

    await this.loadAllKeys();
  }

  ngOnDestroy(): void {
    this.clientSubscription.unsubscribe();
  }

  async generateAPIKey() {
    try {
      this.loading.set(true);
      const result = await this.apiService.generateApiKey();
      this.apiKeys.push(result.apikey);
    } catch {
      this.snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: {
          message: this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
          action: this.translate.instant('OK'),
        },
      });
    } finally {
      this.loading.set(false);
    }
  }

  async deleteAPIKey(key: string) {
    const dialogRef = this.dialog.open(DeleteKeyDialogComponent, {
      autoFocus: false,
      position: {
        top: '50px',
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      try {
        this.loading.set(true);
        await this.apiService.deleteApiKey(key);
        this.apiKeys = this.apiKeys.filter((apiKey) => apiKey.value !== key);
      } catch {
        this.snackBar.openFromComponent(ErrorSnackbarComponent, {
          data: {
            message: this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
            action: this.translate.instant('OK'),
          },
        });
      } finally {
        this.loading.set(false);
      }
    });
  }

  copyToClipboard(keyText: string) {
    copyToClip(keyText).then(() =>
      this.snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: {
          message: this.translate.instant('API_KEYS.COPY_SUCCESS'),
          action: this.translate.instant('OK'),
          type: 'success',
        },
      }),
    );
  }

  private async loadAllKeys() {
    try {
      this.loading.set(true);
      const result = await this.apiService.getApiKeys();
      this.apiKeys = result.apikeys;
    } catch (err) {
      console.log(err);
    } finally {
      this.loading.set(false);
    }
  }
}
